<template>
  <header class="header">
    <div class="header__right">
      <el-dropdown @command="handleCommand">
        <button class="header__user" type="button">
          <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop" alt="管理员头像" />
          <span>
            <strong>{{ userStore.userInfo?.nickname ?? '管理员' }}</strong>
            <small>{{ roleLabel }}</small>
          </span>
          <el-icon><ArrowDown /></el-icon>
        </button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="changePassword">修改密码</el-dropdown-item>
            <el-dropdown-item command="logout">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <el-dialog
      v-model="passwordDialogVisible"
      title="修改密码"
      width="420px"
      @closed="resetPasswordForm"
    >
      <el-form
        ref="passwordFormRef"
        :model="passwordForm"
        :rules="passwordRules"
        label-width="92px"
      >
        <el-form-item label="原密码" prop="oldPassword">
          <el-input
            v-model.trim="passwordForm.oldPassword"
            type="password"
            show-password
            autocomplete="current-password"
            placeholder="请输入原密码"
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model.trim="passwordForm.newPassword"
            type="password"
            show-password
            autocomplete="new-password"
            placeholder="6-64 位新密码"
          />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model.trim="passwordForm.confirmPassword"
            type="password"
            show-password
            autocomplete="new-password"
            placeholder="请再次输入新密码"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="passwordSubmitting" @click="submitPasswordChange">
          保存修改
        </el-button>
      </template>
    </el-dialog>
  </header>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowDown } from '@element-plus/icons-vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { AdminRole } from '@jiaofa/constants'
import { useUserStore } from '@/stores/user'
import { changePassword } from '@/api/auth'

const router = useRouter()
const userStore = useUserStore()
const passwordDialogVisible = ref(false)
const passwordSubmitting = ref(false)
const roleLabels: Record<AdminRole, string> = {
  [AdminRole.SuperAdmin]: '系统超管',
  [AdminRole.OperationAdmin]: '运营管理员',
  [AdminRole.StoreManager]: '门店店长',
  [AdminRole.StoreStaff]: '门店员工',
  [AdminRole.Finance]: '财务人员',
  [AdminRole.CustomerService]: '客服人员',
}
const roleLabel = computed(() => {
  const role = userStore.userInfo?.roles?.[0]
  if (!role) return userStore.isPlatformScope ? '平台管理员' : '门店员工'
  return roleLabels[role as AdminRole] ?? role
})
const passwordFormRef = ref<FormInstance>()
const passwordForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

const passwordRules: FormRules<typeof passwordForm> = {
  oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, max: 64, message: '新密码长度为 6-64 位', trigger: 'blur' },
  ],
  confirmPassword: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    {
      validator: (_rule, value, callback) => {
        if (value !== passwordForm.newPassword) callback(new Error('两次输入的新密码不一致'))
        else callback()
      },
      trigger: 'blur',
    },
  ],
}

const handleCommand = async (command: string) => {
  if (command === 'changePassword') {
    passwordDialogVisible.value = true
    return
  }
  if (command === 'logout') {
    await userStore.logout()
    await router.replace('/login')
  }
}

function resetPasswordForm(): void {
  passwordFormRef.value?.resetFields()
}

async function submitPasswordChange(): Promise<void> {
  const valid = await passwordFormRef.value?.validate().catch(() => false)
  if (!valid) return
  passwordSubmitting.value = true
  try {
    await changePassword({
      oldPassword: passwordForm.oldPassword,
      newPassword: passwordForm.newPassword,
    })
    ElMessage.success('密码修改成功')
    passwordDialogVisible.value = false
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '密码修改失败')
  } finally {
    passwordSubmitting.value = false
  }
}
</script>

<style scoped lang="scss">
.header { height: 64px; padding: 0 24px; flex: 0 0 auto; display: flex; align-items: center; justify-content: flex-end; background: #fff; border-bottom: 1px solid var(--jf-color-border); z-index: 2; }
.header__right { margin-left: auto; display: flex; align-items: center; gap: 20px; }
.header__user { min-height: 40px; padding-left: 16px; border: 0; border-left: 1px solid var(--jf-color-border); display: inline-flex; align-items: center; gap: 10px; background: transparent; color: var(--jf-color-text-primary); cursor: pointer; text-align: left; }
.header__user img { width: 32px; height: 32px; border-radius: 999px; object-fit: cover; background: var(--jf-color-primary-light); }
.header__user span { min-width: 62px; display: flex; flex-direction: column; line-height: 1.25; }
.header__user strong { font-size: 14px; font-weight: 700; }
.header__user small { margin-top: 2px; color: var(--jf-color-text-secondary); font-size: 10px; }
.header__user > .el-icon { color: var(--jf-color-text-secondary); }
</style>
